import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { Button, Col, Input, Row } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { CloseIcon } from '@/assets/icons/v2';
import { PaginationFooter } from '@/components/PaginationFooter';

import { getSelectAllLabel } from './utils';
import { SegmentItem } from './SegmentItem';
import { CloseButton, DrawerContent, LinkButton, SegmentList, SegmentListArea, TlidDrawerStyled } from './styles';
import type { SegmentOption } from './utils';

const DRAWER_WIDTH = 568;
const CLOSE_ICON_SIZE = 16;
const SEGMENTS_PER_PAGE = 15;
const FIRST_PAGE = 1;
const NONE = 0;

interface TlidDrawerProps {
	fileName: string;
	options: SegmentOption[];
	selectedSegmentIds: string[];
	onClose: () => void;
	onSave: (segmentIds: string[]) => void;
}

function findCheckedReportIds(options: SegmentOption[], checkedIds: string[]): Set<string> {
	const checked = options.filter((option) => checkedIds.includes(option.id));
	const reportIds = checked.map((option) => option.reportId);
	const result = new Set(reportIds);

	return result;
}

export function TlidDrawer({
	fileName,
	options,
	selectedSegmentIds,
	onClose,
	onSave
}: Readonly<TlidDrawerProps>): JSX.Element {
	const [checkedIds, setCheckedIds] = useState<string[]>(selectedSegmentIds);
	const [search, setSearch] = useState<string>('');
	const [page, setPage] = useState<number>(FIRST_PAGE);

	const checkedReportIds = findCheckedReportIds(options, checkedIds);
	const hasSelectedTlid = checkedReportIds.size > NONE;

	let scopedOptions = options;

	if (hasSelectedTlid) {
		scopedOptions = options.filter((option) => checkedReportIds.has(option.reportId));
	}

	const term = search.trim().toLowerCase();
	const visibleOptions = scopedOptions.filter((option) => option.label.toLowerCase().includes(term));
	const selectableOptions = visibleOptions.filter((option) => !option.isDisabled);
	const isAllSelected =
		selectableOptions.length > NONE && selectableOptions.every((option) => checkedIds.includes(option.id));
	const selectAllLabel = getSelectAllLabel(isAllSelected);
	const firstIndex = (page - FIRST_PAGE) * SEGMENTS_PER_PAGE;
	const pageOptions = visibleOptions.slice(firstIndex, firstIndex + SEGMENTS_PER_PAGE);

	function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>): void {
		setSearch(event.target.value);
		setPage(FIRST_PAGE);
	}

	function handleToggleSegment(isChecked: boolean, segmentId: string): void {
		setPage(FIRST_PAGE);

		if (isChecked) {
			setCheckedIds([...checkedIds, segmentId]);
			return;
		}

		setCheckedIds(checkedIds.filter((id) => id !== segmentId));
	}

	function handleToggleAll(): void {
		const selectableIds = selectableOptions.map((option) => option.id);

		setPage(FIRST_PAGE);

		if (isAllSelected) {
			setCheckedIds(checkedIds.filter((id) => !selectableIds.includes(id)));
			return;
		}

		const merged = new Set([...checkedIds, ...selectableIds]);

		setCheckedIds(Array.from(merged));
	}

	function handleSave(): void {
		onSave(checkedIds);
	}

	function toSegmentItem(option: SegmentOption): JSX.Element {
		return (
			<SegmentItem
				label={option.label}
				segmentId={option.id}
				checked={checkedIds.includes(option.id)}
				disabled={option.isDisabled}
				onToggle={handleToggleSegment}
			/>
		);
	}

	const footer = (
		<Row justify="center" gutter={16}>
			<Col>
				<Button onClick={onClose}>{I18n.get('Close')}</Button>
			</Col>
			<Col>
				<Button type="primary" onClick={handleSave}>
					{I18n.get('Save')}
				</Button>
			</Col>
		</Row>
	);

	const closeButton = (
		<CloseButton type="text" aria-label={I18n.get('Close')} onClick={onClose}>
			<CloseIcon width={CLOSE_ICON_SIZE} height={CLOSE_ICON_SIZE} />
		</CloseButton>
	);

	return (
		<TlidDrawerStyled
			open
			closable={false}
			extra={closeButton}
			onClose={onClose}
			title={fileName}
			width={DRAWER_WIDTH}
			footer={footer}
		>
			<DrawerContent>
				<SegmentListArea>
					<Row gutter={[0, 16]}>
						<Col span={24}>
							<Input
								allowClear
								value={search}
								prefix={<SearchOutlined />}
								placeholder={I18n.get('Search TLID')}
								onChange={handleSearchChange}
							/>
						</Col>
						{hasSelectedTlid && (
							<Col span={24} style={{ textAlign: 'right' }}>
								<LinkButton type="link" onClick={handleToggleAll}>
									{I18n.get(selectAllLabel)}
								</LinkButton>
							</Col>
						)}
						<Col span={24}>
							<SegmentList
								rowKey="id"
								split={false}
								dataSource={pageOptions}
								renderItem={toSegmentItem}
							/>
						</Col>
					</Row>
				</SegmentListArea>
				<PaginationFooter
					page={page}
					total={visibleOptions.length}
					pageSize={SEGMENTS_PER_PAGE}
					showSizeChanger={false}
					onPageChange={setPage}
				/>
			</DrawerContent>
		</TlidDrawerStyled>
	);
}
